import { Dialog as MuiDialog, type DialogProps as MuiDialogProps, styled, type Theme, useMediaQuery } from '@mui/material';
import { type FormEventHandler, type MouseEvent, type ReactNode, useId } from 'react';

import type { WithoutStyleOverrides } from '../../../types/WithoutStyleOverrides';
import { DialogContent } from '../../atoms/DialogContent';
import { DialogActions, type DialogActionsProps } from '../../molecules/DialogActions';
import { DialogTitle } from '../../molecules/DialogTitle';

const FULL_SCREEN_BREAKPOINT = 'sm';
const CLOSE_BUTTON_REASON = 'closeButtonClick';

const DialogForm = styled('form')({
  display: 'flex',
  flexDirection: 'column',
  flex: '1 1 auto',
  minHeight: 0,
});

type DialogCloseReason = 'backdropClick' | 'escapeKeyDown' | typeof CLOSE_BUTTON_REASON;

type Props = WithoutStyleOverrides<
  Omit<
    MuiDialogProps,
    'slots' | 'slotProps' | 'PaperComponent' | 'title' | 'children' | 'aria-labelledby' | 'onSubmit' | 'onClose'
  >
> & {
  title: ReactNode;
  children: ReactNode;
  closeLabel: string;
  onClose: (event: object, reason: DialogCloseReason) => void;
  actions?: DialogActionsProps;
  onSubmit?: FormEventHandler<HTMLFormElement>;
};

function isSmallScreen(theme: Theme) {
  return theme.breakpoints.down(FULL_SCREEN_BREAKPOINT);
}

export function Dialog(props: Props) {
  const { title, children, closeLabel, onClose, actions, onSubmit, fullScreen = false, ...dialogProps } = props;
  const titleId = useId();
  const smallScreen = useMediaQuery(isSmallScreen);

  function handleCloseButtonClick(event: MouseEvent<HTMLButtonElement>) {
    onClose(event, CLOSE_BUTTON_REASON);
  }

  const body = (
    <>
      <DialogTitle id={titleId} closeLabel={closeLabel} onClose={handleCloseButtonClick}>
        {title}
      </DialogTitle>
      <DialogContent>{children}</DialogContent>
      {actions === undefined ? null : <DialogActions {...actions} />}
    </>
  );

  return (
    <MuiDialog {...dialogProps} onClose={onClose} fullScreen={fullScreen || smallScreen} aria-labelledby={titleId}>
      {onSubmit === undefined ? body : <DialogForm onSubmit={onSubmit}>{body}</DialogForm>}
    </MuiDialog>
  );
}

export type { DialogCloseReason, Props as DialogProps };
